import { getFeatureConfig } from '@luna/feature-config';
import { createEntity, request } from '@luna/api';
import { pick } from '@luna/utils';
import { getEnv } from '@luna/env';
import type { RetailEnvironmentConfig } from '@retail/core-config';

import { filterPlugins } from './parseService';
import {
    loadActivePlugins,
    preparePluginFlagsForActivation,
    preparePluginFlagsForDeactivation,
} from './pluginsService';
import type { Plugin, PluginProductCode, PluginsEndpointResponse } from './types';
import { getPluginModule } from './registerPluginModule';

/** main slice */
const pluginsEntitiesSlice = createEntity({
    name: 'plugins',
    withEntityType: {} as Plugin,
    payloadCreators: {
        queryAll: async () => {
            if (!getFeatureConfig((features) => features.plugins.enabled)) {
                return [];
            }

            const response = await request<PluginsEndpointResponse>(
                `${getEnv<RetailEnvironmentConfig>().prestoUrl}netbanking/my/plugins`,
            );
            const parsedPlugins = filterPlugins(response?.plugins ?? []);

            await loadActivePlugins(parsedPlugins);

            return parsedPlugins;
        },
        updateById: ({ changes }) => {
            return changes as Promise<Plugin>;
        },
    },
    selectId: (plugin) => plugin.productCode,
});

const {
    useEntities: usePlugins,
    queryAllIfNeeded: fetchPluginsIfNeeded,
    queryAll: fetchPlugins,
    invalidate: invalidatePluginsData,
    selectAll: selectAllPlugins,
} = pluginsEntitiesSlice;

export { usePlugins, fetchPluginsIfNeeded, fetchPlugins, invalidatePluginsData, selectAllPlugins };

export const { updateById, selectById, selectSlice } = pluginsEntitiesSlice;

/** extra reducers logic */
export function activatePlugin(payload: { productCode: string; isFree: boolean }): void {
    const plugin = selectById(payload.productCode);

    const flags = preparePluginFlagsForActivation(plugin);
    const settlementAccount = payload.isFree ? undefined : plugin?.settlementAccount;

    updateById({
        id: payload.productCode,
        changes: {
            ...plugin,
            flags,
            dateOfActivation: new Date().toString(),
            settlementAccount,
        },
    });
}

export function deactivatePlugin({ productCode }: { productCode: PluginProductCode }): void {
    const plugin = selectById(productCode);
    const flags = preparePluginFlagsForDeactivation(plugin);

    updateById({
        id: productCode,
        changes: {
            ...plugin,
            ...pick({ flags }, Boolean),
        },
    });

    getPluginModule(productCode)?.onPluginDeactivation?.();
}
